import React, { FocusEventHandler, KeyboardEventHandler, MouseEventHandler } from 'react';
import MuiIconButton from '@mui/material/IconButton';
import type { TokenColor } from '../../setup';
import { styled } from '../../styles';
import { Tooltip, TooltipProps } from '../Tooltip/Tooltip';
import { CircularProgress } from '../Progress/CircularProgress';
import { BpDesignSystem } from '../../types';
import { IconName } from '../Icon/types';
import { Icon, IconProps } from '../Icon/Icon';
import { tokenToPx } from '../../setup/theme/impl/getToken';

type ButtonSize = 'x-small' | 'small' | 'large' | 'medium' | number | undefined;

export type ButtonColor = 'active' | 'default' | TokenColor;

export type IconButtonProps = BpDesignSystem.Props<
  Omit<IconProps, 'name' | 'size' | 'color'> & {
    slotProps?: {
      tooltip?: Omit<TooltipProps, 'children'>;
      icon?: Omit<IconProps, 'name'>;
    };
    iconName: IconName;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    onClickCapture?: MouseEventHandler<HTMLButtonElement>;
    onBlur?: FocusEventHandler<HTMLButtonElement>;
    onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    loading?: boolean;
    /**
     * @deprecated use slotProps.tooltip.title instead
     */
    tooltipText?: string;
    disabledToolTipText?: string;
    /**
     * @deprecated use slotProps.tooltip.placement instead
     */
    tooltipPlacement?: TooltipProps['placement'];
    tabIndex?: number;
    children?: React.ReactNode;
    buttonId?: string;
    size?: ButtonSize;
    color?: ButtonColor;
    type?: 'button' | 'submit' | 'reset' | undefined;
  }
>;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      iconName,
      color,
      className,
      style,
      size = 'medium',
      tooltipText,
      tooltipPlacement,
      dataTest,
      children,
      disabledToolTipText,
      buttonId,
      action,
      slotProps,
      loading,
      disabled,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const iconStyle = { opacity: isDisabled ? 0.55 : 1 };
    const targetTooltipText = slotProps?.tooltip?.title ?? tooltipText ?? '';
    const targetTooltipPlacement = slotProps?.tooltip?.placement ?? tooltipPlacement;

    return (
      <Tooltip
        {...slotProps?.tooltip}
        title={isDisabled ? (disabledToolTipText ?? '') : targetTooltipText}
        placement={targetTooltipPlacement}
        disabled={disabledToolTipText ? false : isDisabled || !targetTooltipText}
      >
        <StyledIconButton
          $size={size}
          color={color === 'active' ? 'primary' : undefined}
          className={className}
          style={style}
          data-test={dataTest}
          disabled={isDisabled}
          {...rest}
          id={buttonId}
          ref={ref}
        >
          <Icon
            name={iconName}
            size={getIconSize(size)}
            color={getIconColor(color)}
            style={iconStyle}
            dataTest={`icon__${dataTest}`}
            {...slotProps?.icon}
          />
          {children}
          {loading && <CircularProgress size={getLoadingSize(size)} shape="circle" />}
        </StyledIconButton>
      </Tooltip>
    );
  }
);

const StyledIconButton = styled(MuiIconButton)<{ $size: ButtonSize }>`
  &.MuiIconButton-root {
    padding: ${({ $size }) => getButtonPadding($size)};
  }
  .loading__component__wrapper {
    position: absolute;
  }
`;

const getIconColor = (color: ButtonColor | undefined): TokenColor | undefined => {
  if (color === 'active') return 'colorPrimary';
  if (color === 'default') return undefined;
  return color;
};

const getButtonPadding = (size: ButtonSize): string | undefined => {
  if (size === 'x-small') return tokenToPx('spacingXxs');
  return tokenToPx('spacingXs');
};

const getIconSize = (size: ButtonSize): IconProps['size'] => {
  if (size === 'x-small') return 16;
  if (size === 'small') return size;
  if (size === 'medium') return size;
  if (size === 'large') return size;
  // size - 16 = size - (tokens.source.spacingXs * 2);
  return typeof size === 'number' ? size - 16 : 32;
};

const getLoadingSize = (size: ButtonSize): number => {
  if (size === 'x-small') return 24;
  if (size === 'small') return 32;
  if (size === 'medium') return 40;
  if (size === 'large') return 56;
  // size - 16 = size - (tokens.source.spacingXs * 2);
  return typeof size === 'number' ? size - 16 : 32;
};
